import React, { useState } from 'react';
import axios from 'axios';
import api from '../../services/api';
import useAuth from '../../hooks/useAuth';
import './OrderDetailsModal.css';

const OrderDetailsModal = ({ order, isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editData, setEditData] = useState({});
  const [users, setUsers] = useState([]);
  const [notesCharCount, setNotesCharCount] = useState(0);
  const [reprintMessage, setReprintMessage] = useState(null);
  const [reprintMessageType, setReprintMessageType] = useState(null);
  const [newDocuments, setNewDocuments] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  // Update editData when order changes
  React.useEffect(() => {
    if (order) {
      console.log('Order changed, updating editData. Order:', order);
      setEditData({
        ...order,
        customer_po_number: order.po_number || '',
        salesperson_id: order.salesperson_id || '', // Initialize with existing salesperson_id if available
      });
      setNotesCharCount(order.notes?.length || 0);
      setExistingDocuments(order.documents || []);
      setNewDocuments([]);
      setIsEditing(false);
      setError(null);
    }
  }, [order]);

  // Fetch active users for salesperson dropdown
  React.useEffect(() => {
    if (isEditing) {
      fetchUsersAndSetSalesperson();
    }
  }, [isEditing]);

  // Fetch users and then set the salesperson
  const fetchUsersAndSetSalesperson = async () => {
    try {
      const response = await api.get('/special-orders/users/active');
      const usersList = response.data.users || [];
      setUsers(usersList);

      console.log('Fetched users:', usersList);
      console.log('Current order sales_person:', order?.sales_person);

      if (!order?.sales_person) {
        console.log('No sales_person in order, skipping match');
        return;
      }

      const searchTerm = order.sales_person.toLowerCase().trim();

      // Find the user that matches the current sales_person name
      // Try multiple matching strategies
      let matchingUser = usersList.find(
        (user) => user.display_name?.toLowerCase() === searchTerm
      );

      // If exact match on display_name doesn't work, check if sales_person matches first name
      if (!matchingUser) {
        matchingUser = usersList.find((user) => {
          const firstName = user.display_name?.split(' ')[0]?.toLowerCase() || '';
          return firstName === searchTerm;
        });
      }

      // Try matching by username (email)
      if (!matchingUser) {
        matchingUser = usersList.find(
          (user) => user.username?.toLowerCase() === searchTerm
        );
      }

      // Try checking if sales_person is contained within display_name (partial match)
      if (!matchingUser) {
        matchingUser = usersList.find((user) =>
          user.display_name?.toLowerCase().includes(searchTerm)
        );
      }

      console.log('Matching user found:', matchingUser);

      if (matchingUser) {
        console.log('Setting salesperson_id to:', matchingUser.id);
        setEditData((prevData) => ({
          ...prevData,
          salesperson_id: matchingUser.id,
        }));
      } else {
        console.warn('No matching user found for salesperson:', order?.sales_person);
        console.log('Available users display names:', usersList.map((u) => u.display_name));
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Handle numeric fields
    if (name === 'quantity' || name === 'price') {
      processedValue = parseFloat(value) || '';
    }

    // Handle notes character count
    if (name === 'notes') {
      setNotesCharCount(value.length);
    }

    setEditData({
      ...editData,
      [name]: processedValue,
    });
  };

  // Document handling functions
  const handleDocumentUpload = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
    e.target.value = '';
  };

  const processFiles = (files) => {
    if (files.length === 0) return;
    const newDocs = files.map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      id: Date.now() + Math.random().toString(36).substr(2, 9)
    }));
    setNewDocuments(prev => [...prev, ...newDocs]);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const handleRemoveExistingDocument = (index) => {
    setExistingDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveNewDocument = (docId) => {
    setNewDocuments(prev => prev.filter(doc => doc.id !== docId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return '🖼️';
    if (type === 'application/pdf') return '📄';
    if (type?.includes('word') || type?.includes('document')) return '📝';
    if (type?.includes('excel') || type?.includes('spreadsheet')) return '📊';
    return '📎';
  };

  const handleSaveEdit = async () => {
    setLoading(true);
    setError(null);

    try {
      // Use FormData to handle file uploads
      const formData = new FormData();

      // Add form fields
      formData.append('customer_name', editData.customer_name);
      formData.append('customer_phone', editData.customer_phone || '');
      formData.append('customer_po_number', editData.customer_po_number || '');
      formData.append('item_name', editData.item_name);
      formData.append('item_color', editData.item_color || '');
      formData.append('item_sku', editData.item_sku || '');
      formData.append('item_size', editData.item_size || '');
      formData.append('quantity', editData.quantity);
      formData.append('price', editData.price);
      formData.append('payment_status', editData.payment_status);
      formData.append('salesperson_id', editData.salesperson_id || '');
      formData.append('notes', editData.notes || '');

      // Add existing documents to keep (as JSON string)
      formData.append('existingDocuments', JSON.stringify(existingDocuments));

      // Add new documents
      newDocuments.forEach((doc) => {
        formData.append('documents', doc.file);
      });

      const baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';
      await axios.put(`${baseURL}/special-orders/orders/${order.id}`, formData, {
        withCredentials: true,
      });

      setIsEditing(false);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update order');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
      setLoading(true);
      setError(null);

      try {
        await api.delete(`/special-orders/orders/${order.id}`);
        onSuccess?.();
        onClose();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete order');
        setLoading(false);
      }
    }
  };

  const handleReprint = async () => {
    try {
      setLoading(true);
      setReprintMessage(null);
      setError(null);
      console.log(`Attempting to reprint order #${order.id}`);

      const response = await api.post(`/special-orders/orders/${order.id}/reprint`);
      console.log('Reprint response:', response.data);

      if (response.data?.success) {
        setReprintMessage(`✓ Order sent to printer successfully! Print Job ID: ${response.data?.printJobId || 'N/A'}`);
        setReprintMessageType('success');
        // Auto-clear after 5 seconds
        setTimeout(() => {
          setReprintMessage(null);
        }, 5000);
      } else {
        const errorMsg = response.data?.message || 'Failed to reprint order';
        setReprintMessage(errorMsg);
        setReprintMessageType('error');
        setError(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to reprint order';
      console.error('Reprint error:', err);
      setReprintMessage(`Error: ${errorMsg}`);
      setReprintMessageType('error');
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !order) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPaymentStatusColor = (status) => {
    return status === 'Paid in Full' ? 'status-paid' : 'status-unpaid';
  };

  return (
    <div className={`modal-overlay ${isOpen ? 'active' : ''}`} onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <h2>{isEditing ? 'Edit Order' : 'Order Details'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* Error Message */}
        {error && <div className="error-message">{error}</div>}

        {/* Reprint Message */}
        {reprintMessage && (
          <div className={`reprint-message ${reprintMessageType}`} style={{
            padding: '12px 16px',
            borderRadius: '4px',
            marginBottom: '16px',
            fontSize: '14px',
            fontWeight: '600',
            backgroundColor: reprintMessageType === 'success' ? '#d4edda' : '#fadbd8',
            color: reprintMessageType === 'success' ? '#155724' : '#c0392b',
            border: `1px solid ${reprintMessageType === 'success' ? '#c3e6cb' : '#e74c3c'}`,
          }}>
            {reprintMessage}
          </div>
        )}

        {/* Modal Body */}
        <div className="modal-body">
          {!isEditing ? (
            // VIEW MODE
            <>
              {/* Order Header */}
              <div className="order-header-info">
                <div className="info-item">
                  <span className="label">Order ID</span>
                  <span className="value">{order.id}</span>
                </div>
                <div className="info-item">
                  <span className="label">Created</span>
                  <span className="value">{formatDate(order.created_at)}</span>
                </div>
                <div className="info-item">
                  <span className="label">Payment Status</span>
                  <span className={`badge ${getPaymentStatusColor(order.payment_status)}`}>
                    {order.payment_status}
                  </span>
                </div>
              </div>

              {/* Order Confirmation Documents Section */}
              {order.documents && order.documents.length > 0 && (
                <div className="modal-section order-confirmation-section">
                  <h4>Order Confirmation</h4>
                  <div className="confirmation-documents">
                    {order.documents.map((doc, index) => (
                      <a
                        key={index}
                        href={`${process.env.REACT_APP_API_URL || ''}${doc.path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="confirmation-doc-item"
                      >
                        <span className="doc-icon">
                          {doc.mimetype?.startsWith('image/') ? '🖼️' :
                           doc.mimetype === 'application/pdf' ? '📄' :
                           doc.mimetype?.includes('word') ? '📝' :
                           doc.mimetype?.includes('excel') || doc.mimetype?.includes('spreadsheet') ? '📊' : '📎'}
                        </span>
                        <div className="doc-details">
                          <span className="doc-name">{doc.originalName || doc.filename}</span>
                          <span className="doc-size">
                            {doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : ''}
                          </span>
                        </div>
                        <span className="doc-download-icon">↗</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Customer Section */}
              <div className="modal-section">
                <h4>Customer Information</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Name</span>
                    <span className="value">{order.customer_name}</span>
                  </div>
                  {order.customer_phone && (
                    <div className="info-item">
                      <span className="label">Phone</span>
                      <span className="value">{order.customer_phone}</span>
                    </div>
                  )}
                  {order.po_number && (
                    <div className="info-item">
                      <span className="label">PO Number</span>
                      <span className="value">{order.po_number}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Item Section */}
              <div className="modal-section">
                <h4>Item Information</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Name</span>
                    <span className="value">{order.item_name}</span>
                  </div>
                  {order.item_color && (
                    <div className="info-item">
                      <span className="label">Color</span>
                      <span className="value">{order.item_color}</span>
                    </div>
                  )}
                  {order.item_sku && (
                    <div className="info-item">
                      <span className="label">SKU</span>
                      <span className="value">{order.item_sku}</span>
                    </div>
                  )}
                  {order.item_size && (
                    <div className="info-item">
                      <span className="label">Size</span>
                      <span className="value">{order.item_size}</span>
                    </div>
                  )}
                  <div className="info-item">
                    <span className="label">Quantity</span>
                    <span className="value">{order.quantity}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Price</span>
                    <span className="value">${parseFloat(order.price).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              {order.salesperson_id || order.sales_person || order.notes ? (
                <div className="modal-section">
                  {order.salesperson_id || order.sales_person ? (
                    <div className="info-item">
                      <span className="label">Salesperson</span>
                      <span className="value">{order.sales_person || order.salesperson_name || 'N/A'}</span>
                    </div>
                  ) : null}
                  {order.notes && (
                    <div className="info-item full-width">
                      <span className="label">Notes</span>
                      <span className="value notes-text">{order.notes}</span>
                    </div>
                  )}
                </div>
              ) : null}
            </>
          ) : (
            // EDIT MODE
            <form className="edit-form">
              {/* Customer Section */}
              <div className="form-section-edit">
                <h4>Customer Information</h4>

                <div className="form-group-edit">
                  <label>Customer Name</label>
                  <input
                    type="text"
                    name="customer_name"
                    value={editData.customer_name}
                    onChange={handleEditChange}
                    maxLength="255"
                  />
                </div>

                <div className="form-row-edit">
                  <div className="form-group-edit">
                    <label>Phone</label>
                    <input
                      type="tel"
                      name="customer_phone"
                      value={editData.customer_phone || ''}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="form-group-edit">
                    <label>PO Number</label>
                    <input
                      type="text"
                      name="customer_po_number"
                      value={editData.customer_po_number || ''}
                      onChange={handleEditChange}
                      maxLength="100"
                    />
                  </div>
                </div>
              </div>

              {/* Item Section */}
              <div className="form-section-edit">
                <h4>Item Information</h4>

                <div className="form-row-edit">
                  <div className="form-group-edit">
                    <label>Item Name</label>
                    <input
                      type="text"
                      name="item_name"
                      value={editData.item_name}
                      onChange={handleEditChange}
                      maxLength="255"
                    />
                  </div>
                  <div className="form-group-edit">
                    <label>Item Color</label>
                    <input
                      type="text"
                      name="item_color"
                      value={editData.item_color || ''}
                      onChange={handleEditChange}
                      maxLength="100"
                    />
                  </div>
                </div>

                <div className="form-row-edit">
                  <div className="form-group-edit">
                    <label>SKU</label>
                    <input
                      type="text"
                      name="item_sku"
                      value={editData.item_sku || ''}
                      onChange={handleEditChange}
                      maxLength="100"
                    />
                  </div>
                  <div className="form-group-edit">
                    <label>Size</label>
                    <input
                      type="text"
                      name="item_size"
                      value={editData.item_size || ''}
                      onChange={handleEditChange}
                      maxLength="50"
                    />
                  </div>
                </div>
              </div>

              {/* Order Details */}
              <div className="form-section-edit">
                <h4>Order Details</h4>

                <div className="form-row-edit">
                  <div className="form-group-edit">
                    <label>Quantity</label>
                    <input
                      type="number"
                      name="quantity"
                      value={editData.quantity}
                      onChange={handleEditChange}
                      min="0.01"
                      step="0.01"
                    />
                  </div>
                  <div className="form-group-edit">
                    <label>Price ($)</label>
                    <input
                      type="number"
                      name="price"
                      value={editData.price}
                      onChange={handleEditChange}
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="form-group-edit">
                    <label>Payment Status</label>
                    <select
                      name="payment_status"
                      value={editData.payment_status}
                      onChange={handleEditChange}
                    >
                      <option value="Not Paid">Not Paid</option>
                      <option value="Paid in Full">Paid in Full</option>
                    </select>
                  </div>
                </div>

                <div className="form-group-edit">
                  <label>Salesperson</label>
                  <select
                    name="salesperson_id"
                    value={editData.salesperson_id || ''}
                    onChange={handleEditChange}
                  >
                    <option value="">-- Select Salesperson (Optional) --</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.display_name || user.username}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group-edit">
                  <label>Notes ({notesCharCount}/1000)</label>
                  <textarea
                    name="notes"
                    value={editData.notes}
                    onChange={handleEditChange}
                    maxLength="1000"
                    rows="3"
                  ></textarea>
                </div>
              </div>

              {/* Order Confirmation Documents Section */}
              <div className="form-section-edit">
                <h4>Order Confirmation</h4>

                {/* Existing Documents */}
                {existingDocuments.length > 0 && (
                  <div className="existing-documents-list">
                    <label>Current Documents</label>
                    {existingDocuments.map((doc, index) => (
                      <div key={index} className="document-item-edit">
                        <span className="document-icon">{getFileIcon(doc.mimetype)}</span>
                        <div className="document-info">
                          <span className="document-name">{doc.originalName || doc.filename}</span>
                          <span className="document-size">{doc.size ? formatFileSize(doc.size) : ''}</span>
                        </div>
                        <button
                          type="button"
                          className="document-remove"
                          onClick={() => handleRemoveExistingDocument(index)}
                          title="Remove document"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* New Documents to Upload */}
                {newDocuments.length > 0 && (
                  <div className="new-documents-list">
                    <label>New Documents to Upload</label>
                    {newDocuments.map((doc) => (
                      <div key={doc.id} className="document-item-edit">
                        <span className="document-icon">{getFileIcon(doc.type)}</span>
                        <div className="document-info">
                          <span className="document-name">{doc.name}</span>
                          <span className="document-size">{formatFileSize(doc.size)}</span>
                        </div>
                        <button
                          type="button"
                          className="document-remove"
                          onClick={() => handleRemoveNewDocument(doc.id)}
                          title="Remove document"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Area */}
                <div
                  className={`document-upload-area-edit ${isDragging ? 'dragging' : ''}`}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    id="document-upload-edit"
                    multiple
                    onChange={handleDocumentUpload}
                    className="document-input"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif"
                  />
                  <label htmlFor="document-upload-edit" className="document-upload-label-edit">
                    <div className="upload-icon">{isDragging ? '📥' : '📁'}</div>
                    <div className="upload-text">
                      <span className="upload-primary">{isDragging ? 'Drop files here' : 'Click to upload'}</span>
                      <span className="upload-secondary">{isDragging ? '' : 'or drag and drop files here'}</span>
                    </div>
                    <span className="upload-hint">PDF, Word, Excel, or Images</span>
                  </label>
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          {!isEditing ? (
            <>
              {isAdmin && (
                <>
                  <button
                    className="btn-edit"
                    onClick={() => setIsEditing(true)}
                    disabled={loading}
                  >
                    Edit Order
                  </button>
                  <button
                    className="btn-delete"
                    onClick={handleDelete}
                    disabled={loading}
                  >
                    Delete Order
                  </button>
                </>
              )}
              <button
                className="btn-reprint"
                onClick={handleReprint}
                disabled={loading}
              >
                Reprint
              </button>
              <button
                className="btn-close"
                onClick={onClose}
                disabled={loading}
              >
                Close
              </button>
            </>
          ) : (
            <>
              <button
                className="btn-cancel"
                onClick={() => {
                  setIsEditing(false);
                  setEditData({ ...order });
                  setError(null);
                }}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="btn-save"
                onClick={handleSaveEdit}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;
